import { OnInit, Component, OnDestroy } from "@angular/core";
import { UtilsService } from "@core/services/utils/utils.service";
import { FieldType } from "@ngx-formly/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "astrobin-formly-field-slider",
  templateUrl: "./formly-field-slider.component.html",
  styleUrls: ["./formly-field-slider.component.scss"]
})
export class FormlyFieldSliderComponent extends FieldType implements OnInit, OnDestroy {
  value: number;
  highValue: number;

  // Flag to identify single value mode
  isSingleValueMode: boolean = false;

  private _destroyed$ = new Subject<void>();
  private _isUpdating = false;

  constructor(private readonly utilsService: UtilsService) {
    super();
  }

  ngOnInit() {
    // Determine if we're in single value mode based on formControl value type and props
    this.isSingleValueMode = typeof this.formControl.value === "number" || this.props.singleValue === true;

    if (this.isSingleValueMode) {
      // Single value mode initialization
      this.value =
        typeof this.formControl.value === "number" ? this.formControl.value : this.props.sliderOptions.floor || 0;

      // Initialize the form control if not already set
      if (this.formControl.value === null || this.formControl.value === undefined) {
        this.formControl.setValue(this.value);
      }

      // Watch for form control changes
      this.formControl.valueChanges.pipe(takeUntil(this._destroyed$)).subscribe(value => {
        if (!this._isUpdating && typeof value === "number") {
          this.value = value;
        }
      });
    } else {
      // Range mode initialization (original behavior)
      this.value = this.formControl.value?.min || this.props.sliderOptions.floor || 0;
      this.highValue = this.formControl.value?.max || this.props.sliderOptions.ceil || 100;

      // Initialize the form control if not already set
      if (!this.formControl.value) {
        this.formControl.setValue({
          min: this.value,
          max: this.highValue
        });
      }

      // Watch for form control changes in range mode
      this.formControl.valueChanges.pipe(takeUntil(this._destroyed$)).subscribe(value => {
        if (!this._isUpdating && value && typeof value === "object") {
          if (value.min !== undefined) {
            this.value = value.min;
          }
          if (value.max !== undefined) {
            this.highValue = value.max;
          }
        }
      });
    }
  }

  ngOnDestroy() {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  onValueChange(value: number) {
    this._isUpdating = true;
    this.value = value; // Update local value immediately
    if (this.isSingleValueMode) {
      // In single value mode, directly set the value
      this.formControl.setValue(value);
    } else {
      // In range mode, update both values using current local values
      this.formControl.patchValue({ min: this.value, max: this.highValue });
    }
    this.utilsService.delay(0).subscribe(() => {
      this._isUpdating = false;
    });
  }

  onHighValueChange(highValue: number) {
    if (!this.isSingleValueMode) {
      this._isUpdating = true;
      this.highValue = highValue; // Update local value immediately
      // Update both values using current local values
      this.formControl.patchValue({ min: this.value, max: this.highValue });
      this.utilsService.delay(0).subscribe(() => {
        this._isUpdating = false;
      });
    }
  }

  onUserChangeEnd(changeContext: any) {
    if (!this.isSingleValueMode) {
      this._isUpdating = true;
      // When user finishes dragging, ensure we update with final values
      const finalValue = { min: this.value, max: this.highValue };
      this.formControl.setValue(finalValue);
      this.utilsService.delay(0).subscribe(() => {
        this._isUpdating = false;
      });
    }
  }
}
